import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { RotateCcw } from 'lucide-react';
import { agentApi } from '../agentApi';
import { AGENT_ADMIN_ROLES, toolLabel } from '../agentContextMap';
import { PMS_DATA_DOMAINS, announcePmsDataChange, usePmsDataRefresh } from '../dataSync';
import { formatTime } from '../format';
import { ACTION_STATUS_LABEL, StatusIcon } from './ApprovalCard';

function ActivityFeed({ user }) {
  const isAdmin = AGENT_ADMIN_ROLES.includes(user.role);
  const [scope, setScope] = useState(isAdmin ? 'all' : 'mine');
  const [status, setStatus] = useState('');
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setActions((await agentApi.listActions({ scope, status: status || undefined, limit: 200 })).actions);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [scope, status]);

  useEffect(() => {
    load();
  }, [load]);

  usePmsDataRefresh(PMS_DATA_DOMAINS, load, { delayMs: 800 });

  const undo = async (id) => {
    try {
      const response = await agentApi.undoAction(id);
      toast.success('Undone');
      // This undo may belong to a conversation that is not open; tell the PMS screens directly.
      if (response?.outcome?.affected) {
        announcePmsDataChange({ ...response.outcome.affected, key: `undo:${id}`, summary: response.outcome.summary, source: 'agent-undo' });
      }
    } catch (error) {
      toast.warn(error.data?.outcome?.error || error.message);
    }
    load();
  };

  return (
    <div className="dagent-panel">
      <div className="dagent-panel__bar">
        <h3>Agent activity</h3>
        <span className="dagent-spacer" />
        {isAdmin && (
          <select className="dagent-select" value={scope} onChange={(event) => setScope(event.target.value)} aria-label="Whose actions">
            <option value="all">All staff</option>
            <option value="mine">Mine</option>
          </select>
        )}
        <select className="dagent-select" value={status} onChange={(event) => setStatus(event.target.value)} aria-label="Status">
          <option value="">Any status</option>
          {Object.entries(ACTION_STATUS_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <button type="button" className="dagent-icon-btn" onClick={load} title="Refresh" disabled={loading}>
          <RotateCcw size={15} aria-hidden="true" />
        </button>
      </div>
      <div className="dagent-table-wrap">
        <table className="dagent-table">
          <thead>
            <tr>
              <th>When</th>
              {scope === 'all' && <th>Staff</th>}
              <th>Action</th>
              <th>Tool</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {actions.map((action) => (
              <tr key={action.id}>
                <td className="dagent-nowrap">{formatTime(action.executedAt || action.createdAt)}</td>
                {scope === 'all' && <td>{action.user?.name || action.user?.email || '—'}</td>}
                <td>
                  {action.summary}
                  {action.error && action.status === 'FAILED' && <div className="dagent-approval__error">{action.error}</div>}
                </td>
                <td className="dagent-nowrap">{toolLabel(action.toolName)}</td>
                <td className="dagent-nowrap">
                  <span className="dagent-inline-status">
                    <StatusIcon status={action.status} /> {ACTION_STATUS_LABEL[action.status] || action.status}
                  </span>
                </td>
                <td>
                  {action.canUndo && (
                    <button type="button" className="dagent-btn dagent-btn--ghost dagent-btn--sm" onClick={() => undo(action.id)}>
                      Undo
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!loading && actions.length === 0 && (
              <tr>
                <td colSpan={6} className="dagent-muted">
                  No agent actions yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ActivityFeed;
